import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, AlertTriangle, CalendarClock, CheckCircle2 } from "lucide-react";

const ICONS = { overdue: CalendarClock, due_soon: CalendarClock, incident: AlertTriangle };
const TONE = { high: "#B84A22", medium: "#D98014", low: "#6D9C55" };

export default function NotificationBell() {
  const [data, setData] = useState({ count: 0, items: [] });
  const navigate = useNavigate();
  const { t } = useLang();

  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="notifications-button" className="relative p-2 rounded-sm hover:bg-[#EAF0EA] transition-all duration-200">
          <Bell className="h-5 w-5 text-[#4B5950]" />
          {data.count > 0 && (
            <span data-testid="notifications-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-[#B84A22] text-white text-[10px] font-bold">
              {data.count > 9 ? "9+" : data.count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="px-4 py-3 border-b border-[#DCE3DD] flex items-center justify-between">
          <span className="font-display font-semibold text-sm">{t("common.notifications")}</span>
          <span className="text-xs text-[#4B5950]">{data.count} {t("common.active")}</span>
        </div>
        <div className="max-h-80 overflow-y-auto es-scroll">
          {data.items.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <CheckCircle2 className="h-8 w-8 text-[#6D9C55] mx-auto" />
              <p className="text-sm text-[#4B5950] mt-2">{t("common.allClear")}</p>
            </div>
          ) : (
            data.items.map((n, i) => {
              const Icon = ICONS[n.kind] || Bell;
              return (
                <button
                  key={`${n.id}-${i}`}
                  data-testid={`notification-${n.kind}-${i}`}
                  onClick={() => navigate(n.link)}
                  className="w-full text-left px-4 py-3 flex gap-3 border-b border-[#DCE3DD] last:border-0 hover:bg-[#F8FAF8] transition-colors"
                >
                  <Icon className="h-4 w-4 mt-0.5 shrink-0" style={{ color: TONE[n.severity] }} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#121A14] truncate">{n.title}</p>
                    <p className="text-xs text-[#4B5950]">{n.detail}</p>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
